import { useState, useEffect } from "react";
import storageService from "../util/storage";
import { checkIfUserIsPartOfOrganisation } from "../features/user/userAPI";
import { useDispatch } from "react-redux";
import { persistUser } from "../features/user/userSlice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
    throw new Error("Missing environment variable: REACT_APP_BACKEND_URL");
}

const isEmail = (identifier) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);

const useAuth = () => {
    const dispatch = useDispatch();
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const makeRequest = async (url, method, body, isRegister) => {
        const response = await fetch(url, {
            method,
            body: JSON.stringify(body),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (!response.ok) {
            const res = await response.json();
            throw new Error(res?.error?.message ?? "Something went wrong");
        }
        const { jwt, user } = await response.json();
        storageService.setItem("token", jwt);
        storageService.setItem("bearer", `Bearer ${jwt}`);
        // if user is using email as identifier then set confirmed to false on register auto login
        if (isEmail(user?.username) && isRegister) {
            user.confirmed = false;
        }
        storageService.setItem("user", user);
        setUser(user);
    };

    const login = async ({ identifier, password }) => {
        try {
            setError(null);
            setIsLoading(true);
            // check if user is part of this organisation
            const user = await checkIfUserIsPartOfOrganisation(identifier);
            if (!user) {
                const errorMessage = "User is not part of this organisation";
                setError(errorMessage);
                console.error(errorMessage);
                setIsLoading(false);
                return errorMessage;
            } else {
                await makeRequest(`${BACKEND_URL}/api/auth/local`, "POST", { identifier, password });

                // Load user up
                dispatch(persistUser(user));

                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            throw new Error("Login failed");
        }
    };

    const register = async ({ MemberNetwork = null, identifier, user_identifier, password, firstname, lastname, phone, email, orgId, DANumber }) => {
        try {
            setIsLoading(true);
            await makeRequest(`${BACKEND_URL}/api/auth/local/register`, "POST", {
                username: identifier.toLowerCase(),
                user_identifier: user_identifier.toLowerCase(),
                ...(MemberNetwork && { memberNetwork: [MemberNetwork] }),
                email: email.toLowerCase(),
                password,
                organisations: [orgId],
                firstname,
                lastname,
                phone,
                confirmed: false,
                ...(DANumber && { DriverAuthorityID: DANumber }),
            }, true);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            if (error.message === "Email or Username are already taken") {
                throw new Error("Email or Username are already taken");
            } else {
                throw new Error("Registration failed");
            }
        }
    };

    const logout = () => {
        storageService.removeItem("token");
        storageService.removeItem("bearer");
        storageService.removeItem("user");
        setUser(null);
    };

    const isLoggedIn = () => {
        const token = storageService.getItem("token");
        // TODO: Validate the token
        return !!token;
    };

    const getToken = () => storageService.getItem("bearer");

    useEffect(() => {
        // const storedUser = storageService.getItem("user");
        // if (storedUser) {
        //     setUser(storedUser);
        // }
    }, [user]);

    return {
        user,
        isLoading,
        error,
        auth: {
            login,
            register,
            logout,
            isLoggedIn,
            getToken,
            getUser: () => storageService.getItem("user") || {},
        },
    };
};

export default useAuth;
